import React from "react";
import { Route, RouteProps, Redirect } from "react-router-dom";
import { useSelector } from "react-redux";
import { EntitlementType } from "../types/EntitlementTypes";
import { useHasAllEntitlements } from "../hooks/useEntitlement";
import {
  selectCallerAccountId,
  selectIsCallerAccountsInvoked,
  selectAccountFeatures,
} from "../store/app-config/AppConfigSelectors";
import PermissionDenied from "./PermissionDenied";
import AppState from "../store/AppState";

interface ProtectedRouteProps extends RouteProps {
  requiredEntitlements?: EntitlementType[];
  requireFeatureFlag?: string;
  showPermissionDenied?: boolean;
  redirectTo?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  requiredEntitlements = [],
  requireFeatureFlag,
  showPermissionDenied = true,
  redirectTo,
  children,
  ...routeProps
}) => {
  const hasRequiredEntitlements = useHasAllEntitlements(requiredEntitlements);
  const accountId = useSelector(selectCallerAccountId);
  const isCallerAccountsInvoked = useSelector(selectIsCallerAccountsInvoked);
  const features = useSelector((state: AppState) =>
    selectAccountFeatures(state, accountId),
  );

  const isLoading = !accountId || !isCallerAccountsInvoked;

  const isFeatureEnabled = requireFeatureFlag
    ? (features?.[requireFeatureFlag] ?? false)
    : true;

  if (requiredEntitlements.length === 0 && !requireFeatureFlag) {
    return <Route {...routeProps}>{children}</Route>;
  }

  if (isLoading) {
    return null;
  }

  if (hasRequiredEntitlements && isFeatureEnabled) {
    return <Route {...routeProps}>{children}</Route>;
  }

  if (redirectTo) {
    return <Redirect to={redirectTo} />;
  }

  if (showPermissionDenied) {
    return (
      <Route {...routeProps}>
        <PermissionDenied />
      </Route>
    );
  }

  return null;
};

export default ProtectedRoute;
